import {
  Component,
  EventEmitter,
  Inject,
  Input,
  type OnInit,
  Output,
} from '@angular/core';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { EnvironmentConfig } from '@core/config-models';
import { TypedComponentRendering } from '@core/jss-models';
import { getFieldValue } from '@core/jss-utils';
import {
  earliestPossibleStartDate,
  getStartDateConfirmationRequired,
  type OrderAddressFormValues,
  selectSelectedInswitchInhouseOption,
  type StartDateOption,
  startDateOptionFormControlId,
  startDateRange,
} from '@innogy/become-a-customer/state';
import {
  CommonUiFormsModule,
  type DropdownItem,
  type RadioItem,
} from '@innogy/common-ui/forms';
import { Store } from '@ngrx/store';
import { FormGroupState, SetUserDefinedPropertyAction } from 'ngrx-forms';
import { CommonModule } from '@angular/common';
import { TrackValueChangeDirective } from '@analytics/events';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import { InfoCardComponent } from '@legacy/common-ui-utility-components';
import { IsInvalidPipe } from '@innogy/utils-deprecated';

import type { ScOrderFields } from '../../../order-fulfillment-container.model';
import { getOrderInswitchInhouseDropdownOptions } from './order-fulfillment-inswitch-inhouse.helpers';

@Component({
  selector: 'wl-order-fulfillment-inswitch-inhouse',
  templateUrl: './order-fulfillment-inswitch-inhouse.component.html',
  styleUrls: [
    './order-fulfillment-inswitch-inhouse.component.ed.scss',
    './order-fulfillment-inswitch-inhouse.component.essent.scss',
  ],
  standalone: true,
  imports: [
    CommonModule,
    TrackValueChangeDirective,
    CommonUiFormsModule,
    AutomationIdDirective,
    AutomationIdDirective,
    IsInvalidPipe,
    InfoCardComponent,
  ],
})
export class OrderFulfillmentInswitchInhouseComponent implements OnInit {
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() labelsShownAboveFields = false;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() formState?: FormGroupState<OrderAddressFormValues>;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering?: TypedComponentRendering<ScOrderFields>;
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() stepNumber!: number;

  @Output() inswitchOpenLegalModal = new EventEmitter<void>();

  fields?: ScOrderFields;
  items: DropdownItem[] | null = null;
  selectedInswitchInhouseOption = this.store.select(
    selectSelectedInswitchInhouseOption
  );

  radioButtonDescriptions: RadioItem<StartDateOption>[] = [];
  noStartDateOptionChosenErrorMessage = '';
  helpText = '';
  startDateOptionControlEnabled = false;

  get switchingSupplier() {
    return (
      this.formState?.value?.inswitchInhouse?.value?.reason === 'other_supplier'
    );
  }

  get startDateChosen() {
    return !!this.formState?.value?.selectedStartDate;
  }

  get optedForCustomStartDate() {
    return this.formState?.value?.startDateOption === 'custom';
  }

  constructor(
    private readonly store: Store,
    @Inject(ENVIRONMENT_CONFIG) private readonly envConfig: EnvironmentConfig
  ) {}

  ngOnInit(): void {
    this.fields = this.rendering?.fields;
    this.items = getOrderInswitchInhouseDropdownOptions(
      this.envConfig.segment,
      this.fields
    );

    this.store.dispatch(
      new SetUserDefinedPropertyAction(
        startDateOptionFormControlId,
        'segment',
        this.envConfig.segment
      )
    );
    const earliestPossibleStartDateString = earliestPossibleStartDate(
      this.envConfig.segment
    ).toLocaleDateString('nl', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const label = getFieldValue(
      this.rendering,
      'EarliestPossibleStartDateRadioButtonLabel',
      ''
    );
    const earliestPossibleStartDateRadioButtonLabel = label.replace(
      '{datum}',
      earliestPossibleStartDateString
    );
    const customStartDateRadioButtonLabel = getFieldValue(
      this.rendering,
      'CustomStartDateRadioButtonLabel',
      ''
    );
    this.radioButtonDescriptions = [
      {
        label: earliestPossibleStartDateRadioButtonLabel,
        value: 'earliestPossible',
      },
      { label: customStartDateRadioButtonLabel, value: 'custom' },
    ];
    this.noStartDateOptionChosenErrorMessage = getFieldValue(
      this.rendering,
      'NoStartDateOptionChosenErrorMessage',
      ''
    );
    this.helpText = getFieldValue(this.rendering, 'HelpText', '');
    this.startDateOptionControlEnabled = getFieldValue(
      this.rendering,
      'StartDateOptionControlEnabled',
      false
    );
  }

  get startDateRange() {
    return startDateRange(
      this.formState?.controls?.inswitchInhouse?.value?.value
        ?.startDateRestrictions
    );
  }

  get startDateConfirmationRequired() {
    return getStartDateConfirmationRequired(this.formState?.controls);
  }

  get selectedSituationLabel() {
    return (
      this.formState?.controls?.inswitchInhouse?.value?.value?.label ||
      this.fields?.DropdownPlaceholder?.value
    );
  }
}
